import React, { useContext, useState, useEffect } from 'react';
import ModalContext from '../../../context/ModalContext.js';
import AuthContext from '../../../context/AuthContext.js';
import DrawerContext from '../../../context/DrawerContext.js';
import { Button, CircularProgress, TextField, Autocomplete } from '@mui/material';

const AddAgentModal = ({ modalDetails, onAdded }) => {
  const modalCtx = useContext(ModalContext);
  const authCtx = useContext(AuthContext);
  const drawerCtx = useContext(DrawerContext);

  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [existingAgent, setExistingAgent] = useState(null);

  useEffect(() => {
    setLoading(true);
    // Fetch users
    fetch(`${process.env.REACT_APP_API_URI}/v3/admin/agent/users`, {
      headers: { Authorization: 'Bearer ' + authCtx.token }
    })
      .then(res => res.json())
      .then(data => {
        setUsers(data.data || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));

    // Fetch existing agents to check for duplicates
    fetch(`${process.env.REACT_APP_API_URI}/v3/admin/agents`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authCtx.token}`
      }
    })
      .then(response => response.json())
      .then(data => {
        setAgents(data.data || []);
      })
      .catch(error => {
        console.error('Error fetching agents:', error);
      });
  }, [authCtx.token]);

  const closeHandle = () => {
    modalCtx.closeModal();
  };

  const handleAdd = () => {
    if (!selectedUser) return;
    
    // Check if user already exists as an agent (by email)
    const existingAgent = agents.find(agent => 
      agent.email?.toLowerCase() === selectedUser.email?.toLowerCase()
    );
    
    if (existingAgent) {
      // User already exists as an agent - show dialog
      setExistingAgent(existingAgent);
      setDuplicateDialogOpen(true);
      return;
    }
    
    // User doesn't exist, proceed with adding
    proceedWithAdd();
  };

  const proceedWithAdd = () => {
    if (!selectedUser) return;
    
    // Close the modal
    modalCtx.closeModal();
    
    // Prepare user data for the drawer
    const userData = {
      userId: selectedUser.id, // Store the userId for API call
      firstName: selectedUser.first_name || '',
      lastName: selectedUser.last_name || '',
      email: selectedUser.email || '',
      phone: selectedUser.phone || '',
      fromModal: true // Flag to indicate this is from modal, so name/email should be non-editable
    };
    
    // Open the drawer with the selected user's details
    drawerCtx.openDrawer();
    drawerCtx.setDetails('add-agent', userData);
    
    if (onAdded) onAdded();
  };

  const handleEditExisting = () => {
    if (!existingAgent) return;
    
    // Close the duplicate dialog and modal
    setDuplicateDialogOpen(false);
    modalCtx.closeModal();
    
    // Prepare agent data for edit mode
    const agentData = {
      id: existingAgent.id,
      firstName: existingAgent.firstName || '',
      lastName: existingAgent.lastName || '',
      email: existingAgent.email || '',
      phone: existingAgent.phone || '',
      status: existingAgent.status || '',
      location: existingAgent.location || '',
      latitude: existingAgent.latitude || '',
      longitude: existingAgent.longitude || '',
      affiliateCode: existingAgent.affiliateCode || ''
    };
    
    // Open the drawer in edit mode
    drawerCtx.openDrawer();
    drawerCtx.setDetails('edit-agent', agentData);
    
    if (onAdded) onAdded();
  };

  const handleCancelDuplicate = () => {
    setDuplicateDialogOpen(false);
    setExistingAgent(null);
  };

  // If duplicate dialog is open, show the duplicate modal instead
  if (duplicateDialogOpen) {
    return (
      <div className="modal-container">
        <div className='modal-header'>
          <h1>Agent Already Exists</h1>
        </div>
        <div className="modal-body">
          <p style={{ textAlign: 'center', fontSize: '1rem', color: '#555', margin: 0 }}>
            This user already exists as an agent ({existingAgent?.firstName} {existingAgent?.lastName}).<br/>
            Would you like to edit the existing agent instead?
          </p>
        </div>
        <div className="modal-footer">
          <Button className="cancel-btn" onClick={handleCancelDuplicate}>CANCEL</Button>
          <Button onClick={handleEditExisting} variant="contained">
            EDIT AGENT
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-container">
      <div className='modal-header'>
        <h1>Add Agent</h1>
      </div>
      <div className="modal-body">
        {loading ? (
          <CircularProgress />
        ) : (
          <Autocomplete
            value={selectedUser}
            onChange={(event, newValue) => setSelectedUser(newValue)}
            options={users}
            getOptionLabel={(option) => {
              if (!option) return '';
              const fullName = `${option.first_name || ''} ${option.last_name || ''}`.trim();
              return fullName ? `${fullName} (${option.email || ''})` : option.email || '';
            }}
            renderOption={(props, option) => {
              const fullName = `${option.first_name || ''} ${option.last_name || ''}`.trim();
              return (
                <li {...props} key={option.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span>{fullName || 'No name'}</span>
                  <span style={{ color: '#888', fontSize: '13px' }}>({option.email || ''})</span>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField {...params} label="Select User" fullWidth />
            )}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            style={{ marginBottom: '16px' }}
          />
        )}
      </div>
      <div className="modal-footer">
        <Button className="cancel-btn" onClick={closeHandle}>CANCEL</Button>
        <Button onClick={handleAdd} disabled={!selectedUser} variant="contained">
          ADD
        </Button>
      </div>
    </div>
  );
};

export default AddAgentModal;

